import { InformationFilled } from '@carbon/icons-react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Accordion,
  Body,
  Button,
  Heading,
  Input,
  Loading,
  StaticBadge,
  SystemMessage,
} from 'vet-bones/design-system/components';
import { formatDate, isEqual } from 'vet-bones/utils';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { PanelProps } from 'src/components/panelStack/PanelStack';
import { PanelWithSidebar } from 'src/components/resourceDialog/PanelWithSidebar';
import { ProjectFileUploadOptionsForm } from 'src/components/resourceDialog/ProjectFileUploadOptionsForm';
import { ResourcePreviewTable } from 'src/components/resourceDialog/ResourcePreviewTable';
import { SidebarFooterButtons } from 'src/components/resourceDialog/SidebarFooterButtons';
import { FileExtension } from 'src/constants/fileExtensions';
import {
  MAX_CSV_PARSE_LINES,
  MAX_CSV_UPLOAD_BYTES,
  MAX_CSV_VISIBLE_LINES,
  MAX_PROJECT_UPLOAD_SOFT_LIMIT,
} from 'src/constants/limits';
import {
  TEST_IDS,
  getProjectFileUploadPanelColumnLabelTestId,
} from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ResourceNode } from 'src/containers/ProjectDiagram/types/diagram';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { didOnlyTheHeaderOptionChange } from 'src/containers/ProjectDiagram/utils/resource/didOnlyTheHeaderOptionChange';
import { getMappingsForResource } from 'src/containers/ProjectDiagram/utils/resource/getMappingsForResource';
import { updateMappingsForResourceFields } from 'src/containers/ProjectDiagram/utils/resource/updateMappingsForResourceFields';
import { updateResourceName } from 'src/containers/ProjectDiagram/utils/resource/updateResourceName';
import { useProjectFileUploadPreviewDialog } from 'src/containers/ProjectFileUploadPreviewDialog/context/useProjectFileUploadPreviewDialog';
import { projectSelector } from 'src/selectors/project';
import { projectFileUploadSelector } from 'src/selectors/projectFileUpload';
import {
  ProjectFileUpload,
  ProjectFileUploadOptions,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { doesFileHaveDuplicateHeaders } from 'src/utils/csv/doesFileHaveDuplicateHeaders';
import { unparseCsv } from 'src/utils/csv/unparseCsv';
import { useProjectFileUploadParser } from 'src/utils/hooks/useProjectFileUploadParser';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import {
  calculateCsvSize,
  calculateProjectSize,
} from 'src/utils/project/calculateProjectSize';
import { readFile } from 'src/utils/readFile';

import styles from 'src/components/resourceDialog/styles/resourceDisplayPanel.module.scss';
import tableStyles from 'src/components/resourceDialog/styles/resourcePreviewTable.module.scss';

const useProjectFileUpload = (fileId?: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(projectFileUploadSelector(projectId, fileId));
};

export const FileUploadPreviewPanel: React.VFC<PanelProps> = () => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const { resourceId } = useReduxSelector(
    (state) => state.ui.fileUploadPreviewDialog
  );

  const {
    setFullSize,
    setDialogTitle,
    setFooterOptions,
    isFileReplaced,
    setIsFileReplaced,
  } = useProjectFileUploadPreviewDialog();

  const [isPropertiesOpen, setIsPropertiesOpen] = React.useState(true);

  const fileUpload = useProjectFileUpload(resourceId);

  const [localFile, setLocalFile] = React.useState<ProjectFileUpload | null>(
    null
  );
  const [localFileObject, setLocalFileObject] = React.useState<File | null>(
    null
  );
  const [localName, setLocalName] = React.useState('');
  const [localOptions, setLocalOptions] =
    React.useState<ProjectFileUploadOptions>(defaultProjectFileUploadOptions);

  // Initialize local state from fileUpload
  const didFileUploadChange = useDidValueChange(fileUpload);
  React.useEffect(() => {
    if (didFileUploadChange && fileUpload) {
      setLocalFile(fileUpload);
      setLocalName(fileUpload.name);
      setLocalOptions(fileUpload.options);
    }
  }, [didFileUploadChange, fileUpload]);

  const { abortParser, isParsing, errors, fields, rows, updateFieldLabel } =
    useProjectFileUploadParser(
      localFileObject || localFile?.contents || null,
      localOptions,
      localFile?.partial ? MAX_CSV_PARSE_LINES : undefined,
      localFile?.fields
    );

  const onDropAccepted = React.useCallback(
    async (files: File[]) => {
      if (!fileUpload) {
        return;
      }

      const [file] = files;
      const currentProjectSize = calculateProjectSize(project);
      const replacedFileSize = calculateCsvSize(fileUpload.contents);
      const doesFilePutProjectOverLimit =
        currentProjectSize - replacedFileSize + file.size >=
        MAX_PROJECT_UPLOAD_SOFT_LIMIT;
      const isFilePartial =
        doesFilePutProjectOverLimit || file.size > MAX_CSV_UPLOAD_BYTES;

      const fileContents = await (isFilePartial
        ? Promise.resolve('')
        : readFile(file));

      const doesFileHaveDupeHeaders = await doesFileHaveDuplicateHeaders(
        fileUpload.options
      )(file);
      const newOptions: ProjectFileUploadOptions = {
        ...fileUpload.options,
        header: doesFileHaveDupeHeaders ? false : fileUpload.options.header,
      };
      const newProjectFileUpload: ProjectFileUpload = {
        id: fileUpload.id,
        path: file.name,
        name: fileUpload.name,
        contents: fileContents,
        fields: [], // updated when saved (after parse)
        options: newOptions,
        dateLastUpdated: fileUpload.dateLastUpdated, // updated when saved (in reducer)
        partial: isFilePartial,
      };
      setIsFileReplaced(true);
      setLocalFile(newProjectFileUpload);
      setLocalFileObject(file);
      setLocalOptions(newOptions);

      if (isFilePartial) {
        dispatch(projectsActionCreators.uploadedPartialCsv());
      }
    },
    [fileUpload, project, setIsFileReplaced, dispatch]
  );

  const { getInputProps, open } = useDropzone({
    accept: [FileExtension.CSV],
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDropAccepted,
  });

  const handleCloseDialog = React.useCallback(() => {
    abortParser.current();
    setIsFileReplaced(false);
    setLocalFile(null);
    setLocalFileObject(null);
    setLocalName('');
    setLocalOptions(defaultProjectFileUploadOptions);
    dispatch(uiSlice.actions.toggledFileUploadPreviewDialog({ isOpen: false }));
  }, [abortParser, dispatch, setIsFileReplaced]);

  const handleSave = React.useCallback(() => {
    if (!localFile || !fileUpload) {
      return;
    }

    // For partial files, generate contents from parsed fields and rows
    const contents =
      localFile.partial && fields.length > 0
        ? unparseCsv({
            fields: fields.map(({ value }) => value),
            rows,
            fileOptions: localOptions,
          })
        : localFile.contents;

    dispatch(
      projectsActionCreators.updatedFileUploadForProject(projectId, {
        name: localName,
        path: localFile.path,
        contents,
        options: localOptions,
        fields,
        id: fileUpload.id,
        partial: localFile.partial,
      })
    );

    if (diagram) {
      commitDiagramModel(diagram, (model) => {
        if (localName !== fileUpload.name) {
          updateResourceName(fileUpload.id, localName)(model);
        }
        const resourceKey = getResourceKey(fileUpload.id);
        const isHeaderOptionChangedOnly = didOnlyTheHeaderOptionChange(
          fileUpload.options,
          localOptions
        );
        updateMappingsForResourceFields({
          areFieldReferencesKept:
            isHeaderOptionChangedOnly &&
            fields.length === fileUpload.fields.length,
          newFields: fields,
          oldFields: fileUpload.fields,
          resourceKey,
        })(model);
      });

      diagram.undoManager.clear();
    }

    handleCloseDialog();
  }, [
    diagram,
    dispatch,
    fields,
    fileUpload,
    handleCloseDialog,
    localFile,
    localName,
    localOptions,
    projectId,
    rows,
  ]);

  const errorText = React.useMemo(() => {
    if (!localOptions.separator) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.separator;
    }
    if (!localOptions.quote) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.quote;
    }
    if (!localOptions.escape) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.escape;
    }
    if (errors.length) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.errors;
    }
    if (!rows.length) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.empty;
    }
    if (!localName) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.name;
    }
    return '';
  }, [errors, localName, localOptions, rows]);

  const warningText = React.useMemo(() => {
    if (!fileUpload) {
      return '';
    }

    const areOptionsChanged = !isEqual(fileUpload.options, localOptions);
    if (areOptionsChanged || isFileReplaced) {
      // Only show warning if the resource has any mappings
      const resourceNode = diagram?.findNodeForKey(
        getResourceKey(fileUpload.id)
      ) as ResourceNode | null;
      const hasMappings =
        resourceNode && getMappingsForResource(resourceNode).length > 0;

      if (hasMappings) {
        return copy.components.projectResourceDialog.projectFileUploadPanel
          .warningTitle;
      }
    }

    return '';
  }, [diagram, fileUpload, isFileReplaced, localOptions]);

  const isDirty = React.useMemo(() => {
    if (!localFile || isFileReplaced) {
      return true;
    }
    const isNameChanged = localName !== localFile.name;
    const areOptionsChanged = !isEqual(localFile.options, localOptions);
    const areFieldsChanged = !isEqual(localFile.fields, fields);

    return isNameChanged || areOptionsChanged || areFieldsChanged;
  }, [localFile, isFileReplaced, localName, localOptions, fields]);

  const handleRevert = React.useCallback(() => {
    setIsFileReplaced(false);
    setLocalFile(fileUpload);
    setLocalFileObject(null);
    setLocalName(fileUpload?.name || '');
    setLocalOptions(fileUpload?.options || defaultProjectFileUploadOptions);
  }, [fileUpload, setIsFileReplaced]);

  // Set up footer and drawer state
  React.useEffect(() => {
    setFullSize(true);
    setDialogTitle(copy.components.fileUploadPreviewDialog.title);

    setFooterOptions({});

    return () => {
      setFullSize(false);
    };
  }, [setFullSize, setDialogTitle, setFooterOptions]);

  const hasErrors = errors.length > 0;
  const visibleRows = rows.slice(0, MAX_CSV_VISIBLE_LINES);

  let preview: JSX.Element | undefined;

  if (!localFile) {
    preview = (
      <div className={styles.previewLoading}>
        <Loading size="lg" />
      </div>
    );
  } else if (isParsing) {
    preview = (
      <div className={styles.previewLoading}>
        <Loading size="lg" />
      </div>
    );
  } else if (hasErrors) {
    preview = (
      <div className="sd-file-upload-panel__error-container">
        <SystemMessage
          text={
            <>
              <Heading size="md">
                {
                  copy.components.projectResourceDialog.projectFileUploadPanel
                    .errors.title
                }
              </Heading>
              {errors.map((error) => (
                <div key={`error-${error.row}-${error.message}`}>
                  {
                    copy.components.projectResourceDialog.projectFileUploadPanel
                      .errors.row
                  }{' '}
                  {error.row}: {error.message}
                </div>
              ))}
            </>
          }
          variant="alert"
        />
      </div>
    );
  } else if (
    !localOptions.separator ||
    !localOptions.quote ||
    !localOptions.escape
  ) {
    preview = (
      <SystemMessage
        text={
          copy.components.projectResourceDialog.fileUploadPanel
            .fillAllProperties
        }
        variant="alert"
      />
    );
  } else if (rows.length === 0) {
    preview = (
      <SystemMessage
        text={
          copy.components.projectResourceDialog.projectFileUploadPanel.errors
            .empty
        }
        variant="info"
      />
    );
  } else {
    const lastModified = localFile.dateLastUpdated
      ? formatDate(new Date(localFile.dateLastUpdated))
      : null;

    preview = (
      <>
        <div className={styles.fileInfo}>
          <div className={styles.headerRow}>
            <Input
              className={styles.fileName}
              clearable={false}
              id="file-name-input"
              label=""
              minimal
              onChange={(e) => {
                setLocalName(e.target.value);
              }}
              placeholder={
                copy.components.projectResourceDialog.fileUploadPanel
                  .fileNamePlaceholder
              }
              size="lg-inside"
              value={localName}
            />
            <div className={styles.headerRight}>
              {lastModified ? (
                <div className={styles.lastUpdated}>
                  {copy.components.projectResourceDialog.fileUploadPanel.lastUpdated(
                    lastModified
                  )}
                  {rows.length >= MAX_CSV_VISIBLE_LINES ? '   /' : null}
                </div>
              ) : null}
              {rows.length >= MAX_CSV_VISIBLE_LINES ? (
                <StaticBadge
                  leftIcon={InformationFilled}
                  size="md"
                  type="info"
                  variant="muted"
                >
                  {
                    copy.components.projectResourceDialog.fileUploadPanel
                      .mayNotReflectFullData
                  }
                </StaticBadge>
              ) : null}
            </div>
          </div>
        </div>
        <ResourcePreviewTable
          fields={fields}
          headerTestIdFn={getProjectFileUploadPanelColumnLabelTestId}
          onFieldLabelChange={updateFieldLabel}
          renderHeaderExtra={(field) => (
            <div className={tableStyles.statistics}>
              <Body ellipsize size="sm">
                {copy.components.projectResourceDialog.projectFileUploadPanel.statistics.distinct(
                  field.statistics.distinct
                )}
              </Body>
              <Body ellipsize size="sm">
                {copy.components.projectResourceDialog.projectFileUploadPanel.statistics.records(
                  field.statistics.records
                )}
              </Body>
            </div>
          )}
          rows={visibleRows}
          testId={TEST_IDS.projectFileUploadPanel.table}
        />
      </>
    );
  }

  const asideHeader = (
    <div className={styles.fileInputContainer}>
      <Input
        clearable={false}
        disabled
        label={copy.components.projectResourceDialog.fileUploadPanel.file}
        size="md"
        value={localFile?.path || ''}
      />
      <Button onClick={open} size="md" variant="secondary">
        {
          copy.components.projectResourceDialog.projectFileUploadPanel.form
            .replace
        }
      </Button>
    </div>
  );

  const asideContent = (
    <Accordion
      icon="left"
      isOpen={isPropertiesOpen}
      label={
        copy.components.projectResourceDialog.fileUploadPanel.resourceProperties
      }
      onInteraction={setIsPropertiesOpen}
      size="md"
    >
      <ProjectFileUploadOptionsForm
        disableHeaderToggle={false}
        fileOptions={localOptions}
        onSetFileOptions={setLocalOptions}
      />
    </Accordion>
  );

  const asideFooter = (
    <SidebarFooterButtons
      isDirty={isDirty}
      isSaveDisabled={Boolean(errorText)}
      onRevert={handleRevert}
      onSave={handleSave}
      revertLabel={copy.components.fileUploadPreviewDialog.revertButton}
      saveLabel={copy.components.fileUploadPreviewDialog.saveButton}
      warningText={warningText}
    />
  );

  return (
    <>
      <PanelWithSidebar
        asideContent={asideContent}
        asideFooter={asideFooter}
        asideHeader={asideHeader}
        mainContent={preview}
      />
      <input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getInputProps()}
      />
    </>
  );
};
